/**
 * The single, cohesive vocabulary for device acquisition.
 *
 * `DeviceOutcome` enumerates every distinguishable result of trying to acquire a
 * microphone or camera. It is the contract between the acquisition core (which
 * knows how to talk to the browser) and callers (which decide what UX to show).
 * Callers switch on this enum; they never inspect DOMExceptions themselves.
 *
 * The names deliberately mirror the de-facto industry taxonomy (Daily's
 * `useDevices` camState/micState) so the states are familiar and exhaustive:
 *   granted | blocked | in-use | not-found | not-supported ...
 */
export type DeviceKind = 'microphone' | 'camera';

export enum DeviceOutcome {
  /** Permission held and a usable device was acquired. */
  Granted = 'granted',
  /**
   * Browser-level permission is blocked (NotAllowedError, and the browser
   * reports the permission as `denied`). User must re-grant — typically a page
   * reload after changing the site permission.
   */
  PermissionDenied = 'permission_denied',
  /**
   * The browser reports permission `granted`, but acquisition still failed with
   * NotAllowedError — the OS/system is blocking the device (e.g. macOS privacy
   * settings, Windows camera privacy). Distinct from PermissionDenied because the
   * fix is in OS settings, not the browser.
   */
  SystemDenied = 'system_denied',
  /** No matching device exists (NotFoundError / OverconstrainedError). */
  NoDevice = 'no_device',
  /**
   * A device exists and permission is held, but it could not be opened
   * (NotReadableError) — busy, claimed by another app, or a hardware fault.
   */
  DeviceUnavailable = 'device_unavailable',
  /**
   * The permission prompt was shown and dismissed without a choice (NotAllowedError
   * while the browser permission remains `prompt`). Recoverable by prompting again —
   * NOT a hard denial. Distinguishing this from PermissionDenied is the cross-browser
   * detection gap called out in HNS-4.
   *
   * Only produced where a denial WOULD have been reported as `denied`. WebKit reports `'prompt'`
   * either way, so on Safari/iOS a NotAllowedError maps to PermissionDenied instead — see
   * `Capabilities.reportsPermissionDenial` (HNS-234).
   */
  Dismissed = 'dismissed',
  /**
   * Media capture is unavailable in this context: no `mediaDevices`/`getUserMedia`
   * (insecure context / old browser) or a SecurityError.
   */
  Unsupported = 'unsupported',
}

/** Reactive, ambient permission status for a device kind (no prompt is triggered to read it). */
export type PermissionStatus = 'unknown' | 'prompt' | 'granted' | 'denied' | 'system_denied' | 'unsupported';

/** The set of failure outcomes — anything that is not `Granted`. */
export const FAILURE_OUTCOMES: ReadonlySet<DeviceOutcome> = new Set([
  DeviceOutcome.PermissionDenied,
  DeviceOutcome.SystemDenied,
  DeviceOutcome.NoDevice,
  DeviceOutcome.DeviceUnavailable,
  DeviceOutcome.Dismissed,
  DeviceOutcome.Unsupported,
]);

export const isFailureOutcome = (outcome: DeviceOutcome): boolean => FAILURE_OUTCOMES.has(outcome);

/**
 * Failure outcomes that a fresh "Allow" prompt cannot resolve: the device could not be
 * opened (missing hardware, busy/unreadable, or an unsupported context), so re-running
 * `getUserMedia` just fails the same way. The join gate uses this to stand its prompt CTA
 * down once a promptable-looking device has failed to acquire — otherwise the button loops
 * forever on a click that can never succeed (e.g. a laptop enumerating its built-in camera
 * while the lid is shut).
 *
 * Deliberately EXCLUDES the verdict-bearing failures: `PermissionDenied`/`SystemDenied` flip
 * the recorded permission (which stands the CTA down on its own and routes to the re-enable
 * flow), and `Dismissed` IS resolvable by prompting again, so its CTA must stay.
 */
export const UNPROMPTABLE_OUTCOMES: ReadonlySet<DeviceOutcome> = new Set([
  DeviceOutcome.NoDevice,
  DeviceOutcome.DeviceUnavailable,
  DeviceOutcome.Unsupported,
]);

export const isUnpromptableOutcome = (outcome: DeviceOutcome): boolean => UNPROMPTABLE_OUTCOMES.has(outcome);
